// Standard library imports
import React, { useContext, useEffect, useState } from 'react';

// External library imports
import { toast } from 'react-toastify';
import { get as lodashGet, set as lodashSet, mean as lodashMean, has as lodashHas } from 'lodash';
import { UilPen } from '@iconscout/react-unicons';
import moment from 'moment';

// Internal module imports
import Typography from '../../../../components/Typography/Typography';
import { useLoader, useUserRole } from '../../../../hooks';
import allDeviceClasses from '../../../../styles/AllDevices.module.css';
import { RootBiddingService } from '../../../../services/BiddingService/Root/BiddingService';
import { momentTimeFormater } from '../../../../utils/dateHelper';
import DatePicker from '../../../../components/Inputs/DatePicker/DatePicker';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import { ConfigContext } from '../../../../context/ConfigContext';
import { formatCustomerName } from '../../../../utils/biddingHelper';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import { RootBidCredentialsService } from '../../../../services/BiddingService/Root/BidCredentialService';
import bidClasses from '../Bidding.module.css';
import Table from '../../../../components/Table/Table';
import IconComponent from '../../../../components/Icon/Icon';
import DeleteModal from '../../../../components/DeleteModal/DeleteModal';
import { UpdateAfrrEnergyBidHour } from './UpdateAfrrEnergyBidHour';
import { AdminBiddingService } from '../../../../services/BiddingService/Admin/BiddingService';
import { AdminBidCredentialsService } from '../../../../services/BiddingService/Admin/BidCredentialService';
import { AuthContext } from '../../../../context/AuthContext';
import Checkbox from '../../../../components/Inputs/Checkbox/Checkbox';

const MARKET_OPTIONS = [
    {
        label: 'FCR-D-INC/FCR-D-DEC',
        value: 'FCR-D',
    },
    {
        label: 'FFR',
        value: 'FFR',
    },
    {
        label: 'aFRR (Capacity)',
        value: 'aFRR-capacity',
    },
    {
        label: 'aFRR (Energy)',
        value: 'aFRR-energy',
    },
    {
        label: 'mFRR (Capacity)',
        value: 'mFRR-capacity',
    },
    {
        label: 'mFRR (Energy)',
        value: 'mFRR-energy',
    },
    {
        label: 'FCR-D-FT',
        value: 'FCR-D-FT',
    },
    {
        label: 'FCR-D-N-NMMS',
        value: 'FCR-D-N-NMMS',
    },
];

const getPostBidDeadLine = (date) => {
    return {
        currentCETTime: momentTimeFormater(momentTimeFormater().format('YYYY-MM-DD HH:mm')),
        'FCR-D': {
            primary: momentTimeFormater(momentTimeFormater(date).subtract(1, 'days').set({ hour: 0, minute: 30, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
            secondary: momentTimeFormater(momentTimeFormater(date).subtract(1, 'days').set({ hour: 18, minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
        },
        FFR: {
            primary: momentTimeFormater(momentTimeFormater(date).subtract(1, 'days').set({ hour: 15, minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
        },
        'aFRR-capacity': {
            primary: momentTimeFormater(momentTimeFormater(date).subtract(1, 'days').set({ hour: 7, minute: 30, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
        },
        'aFRR-energy': {
            primary: momentTimeFormater(momentTimeFormater(date).subtract(1, 'days').set({ hour: 23, minute: 30, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
        },
        'mFRR-capacity': {
            primary: momentTimeFormater(momentTimeFormater(date).subtract(1, 'days').set({ hour: 7, minute: 30, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
        },
        'mFRR-energy': {
            primary: momentTimeFormater(momentTimeFormater(date).subtract(1, 'days').set({ hour: 23, minute: 30, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
        },
        'FCR-D-FT': {
            primary: momentTimeFormater(momentTimeFormater(date).subtract(2, 'days').set({ hour: 8, minute: 30, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
            secondary: {
                start: momentTimeFormater(momentTimeFormater(date).subtract(1, 'days').set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
                end: momentTimeFormater(momentTimeFormater(date).subtract(1, 'days').set({ hour: 15, minute: 30, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
            },
        },
        'FCR-D-N-NMMS': {
            primary: momentTimeFormater(momentTimeFormater(date).subtract(1, 'days').set({ hour: 0, minute: 30, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
            secondary: momentTimeFormater(momentTimeFormater(date).subtract(1, 'days').set({ hour: 18, minute: 0, second: 0, millisecond: 0 }).format('YYYY-MM-DD HH:mm')),
        },
    };
};

const getSelectedMarketCreds = (rawCreds, market, marketGroup, setSelectedBidCred, setBidCredOption) => {
    let credentials = [];
    if (market === 'FCR-D') credentials = rawCreds.filter((cred) => [...marketGroup.FCR_D].some((item) => cred.market.includes(item)));
    else if (market === 'FFR') credentials = rawCreds.filter((cred) => [...marketGroup.FFR].some((item) => cred.market.includes(item)));
    else if (market === 'aFRR-capacity' || market === 'aFRR-energy') credentials = rawCreds.filter((cred) => [...marketGroup.AFRR].some((item) => cred.market.includes(item)));
    else if (market === 'mFRR-capacity' || market === 'mFRR-energy') credentials = rawCreds.filter((cred) => [...marketGroup.MFRR].some((item) => cred.market.includes(item)));
    else if (market === 'FCR-D-FT') credentials = rawCreds.filter((cred) => [...marketGroup.FCR_D_FT].some((item) => cred.market.includes(item)));
    else if (market === 'FCR-D-N-NMMS') credentials = rawCreds.filter((cred) => [...marketGroup.FCR_D_N_NMMS].some((item) => cred.market.includes(item)));

    setSelectedBidCred(lodashGet(credentials, [0], {}));
    setBidCredOption([...credentials]);
    return credentials;
};

const PostBid = ({ data, closeModal, getBids }) => {
    const { state } = useContext(AuthContext);
    const { isRootUser } = useUserRole();
    const service = isRootUser ? RootBiddingService : AdminBiddingService;
    const { bidDate, type } = data;
    const userBidMarkets = lodashGet(state, 'user.config.biddingMarkets');
    const {
        state: { marketGroup },
    } = useContext(ConfigContext);
    const [startLoader, stopLoader] = useLoader();
    const [date, setDate] = useState(moment(bidDate).toDate());
    const [bidsData, setBidsData] = useState({
        normalBids: [],
        bundleBids: [],
    });
    const [selectedMarket, setSelectedMarket] = useState(MARKET_OPTIONS[0]);
    const [postBidDeadLine, setPostBidDeadLine] = useState(getPostBidDeadLine(date));

    const [rawBidCreds, setRawBidCreds] = useState([]);
    const [bidCredOption, setBidCredOption] = useState([]);
    const [selectedBidCred, setSelectedBidCred] = useState({});

    const [lastPostedAt, setLastPostedAt] = useState('');
    const [confirmModal, setConfirmModal] = useState(false);

    const [tableHeadersAndKeys, setTableHeadersAndKeys] = useState({});

    const [modal, setModal] = useState({
        status: false,
        modalId: null,
        data: null,
    });

    const [energyBidsToPost, setEnergyBidsToPost] = useState({});
    const [energyBidType, setEnergyBidType] = useState({});

    useEffect(() => {
        getBidCredentials();
    }, []);

    useEffect(() => {
        if (rawBidCreds.length) getSelectedMarketCreds(rawBidCreds, selectedMarket.value, marketGroup, setSelectedBidCred, setBidCredOption);

        if (selectedMarket?.value === 'aFRR-energy')
            setTableHeadersAndKeys({
                headers: ['Customer Name', 'Instance', 'Market', 'Avg Capacity', 'Action'],
                keys: ['customerName', 'instance', 'market', 'averageCapacity', 'action'],
                bundleBidsHeaders: ['Customer Name', 'Instance Bundle', 'Market', 'Avg Capacity', 'Action'],
                bundleBidsKeys: ['customerName', 'instanceBundle', 'market', 'averageCapacity', 'action'],
            });
        else if (selectedMarket?.value === 'mFRR-energy')
            setTableHeadersAndKeys({
                headers: ['Customer Name', 'Instance', 'Market', 'Avg Capacity', 'Type', 'Action'],
                keys: ['customerName', 'instance', 'market', 'averageCapacity', 'energyBidType', 'action'],
                bundleBidsHeaders: ['Customer Name', 'Instance Bundle', 'Market', 'Avg Capacity', 'Type', 'Action'],
                bundleBidsKeys: ['customerName', 'instanceBundle', 'market', 'averageCapacity', 'energyBidType', 'action'],
            });
        else
            setTableHeadersAndKeys({
                headers: [
                    (selectedMarket?.value === 'aFRR-capacity' || selectedMarket?.value === 'mFRR-capacity') && 'Select',
                    'Customer Name',
                    'Instance',
                    'Market',
                    'Avg Capacity',
                ],
                keys: [
                    (selectedMarket?.value === 'aFRR-capacity' || selectedMarket?.value === 'mFRR-capacity') && 'select',
                    'customerName',
                    'instance',
                    'market',
                    'averageCapacity',
                ],
                bundleBidsHeaders: [
                    (selectedMarket?.value === 'aFRR-capacity' || selectedMarket?.value === 'mFRR-capacity') && 'Select',
                    'Customer Name',
                    'Instance Bundle',
                    'Market',
                    'Avg Capacity',
                ],
                bundleBidsKeys: [
                    (selectedMarket?.value === 'aFRR-capacity' || selectedMarket?.value === 'mFRR-capacity') && 'select',
                    'customerName',
                    'instanceBundle',
                    'market',
                    'averageCapacity',
                ],
            });
    }, [selectedMarket.value]);

    useEffect(() => {
        setPostBidDeadLine(getPostBidDeadLine(date));
    }, [date]);

    useEffect(() => {
        if (selectedBidCred?.value && date) {
            getBidsForPost();
            getBidHistory();
        }
    }, [selectedBidCred?.value, date]);

    const getBidCredentials = () => {
        if (isRootUser) RootBidCredentialsService.GetAll(startLoader, handleBidCredentialSuccess, handleError, stopLoader);
        else {
            const credIds = [];
            for (const market in userBidMarkets) {
                lodashHas(userBidMarkets, `[${market}].credentials`) &&
                    lodashGet(userBidMarkets, `[${market}].enabled`, false) &&
                    credIds.push(lodashGet(userBidMarkets, `[${market}].credentials`));
            }
            AdminBidCredentialsService.GetAll({ credIds }, startLoader, handleBidCredentialSuccess, handleError, stopLoader);
        }
    };

    const handleBidCredentialSuccess = ({ data }) => {
        const bidCredentials = data?.data?.map((item) => {
            return {
                label: item.name,
                value: item._id,
                market: item?.market,
            };
        });
        setRawBidCreds(bidCredentials);
        getSelectedMarketCreds(bidCredentials, selectedMarket.value, marketGroup, setSelectedBidCred, setBidCredOption);
    };

    const getBidHistory = () => {
        const params = {
            credentialId: selectedBidCred.value,
            market: selectedMarket.value,
            date: momentTimeFormater(date).format('YYYY-MM-DD'),
            type,
        };
        service.GetLastDocPosted(params, startLoader, handleBidHistorySuccess, handleError, stopLoader);
    };

    const handleBidHistorySuccess = ({ data }) => setLastPostedAt(lodashGet(data, ['data', 'updatedAt'], ''));

    const getBidsForPost = () => {
        if (selectedMarket.value && selectedBidCred.value) {
            const params = {
                bidCredId: selectedBidCred.value,
                market: selectedMarket.value,
                bidDate: momentTimeFormater(date).format('YYYY-MM-DD'),
                type,
            };

            service.GetBidsForPost(params, startLoader, handleFetchBidsSuccess, handleError, stopLoader);
        }
    };

    const getCheckBox = (item) => {
        return (
            <div className={bidClasses.Flex} style={{ justifyContent: 'center' }}>
                <Checkbox onChange={(isChecked) => getCheckedBids(isChecked, item)} checked={item.checked} />
            </div>
        );
    };

    const getCheckedBids = (checked, tempBid) => {
        const bidType = tempBid.tempBidType;
        setBidsData((prev) => {
            const updatedBid = lodashGet(prev, [bidType], []).map((item) => {
                if (item._id === tempBid._id) {
                    return {
                        ...item,
                        checked: checked,
                        select: getCheckBox({ ...item, checked }),
                    };
                }
                return item;
            });
            return {
                ...prev,
                [bidType]: updatedBid,
            };
        });
    };

    const handleFetchBidsSuccess = ({ data }) => {
        const tempRegulationPrice = {};
        const normalBids = [];
        const bundleBids = [];
        const tempEnergyBidType = {};
        ['normalBids', 'bundleBids'].map((el) => {
            lodashGet(data, ['data', el], []).map((item) => {
                const temp = {
                    checked: item.checked,
                    _id: item._id,
                    ...(el === 'normalBids'
                        ? { customerName: lodashGet(item, ['customer', 0, 'name']), instance: lodashGet(item, ['instance', 'name']) }
                        : { customerName: formatCustomerName(item.customer), instanceBundle: lodashGet(item, ['instanceBundle', 'name']) }),
                    market: item.market,
                    averageCapacity: lodashGet(item, [type, 'capacity'], []).length ? lodashMean(lodashGet(item, [type, 'capacity'])).toFixed(2) : '--',
                };

                if (selectedMarket.value === 'aFRR-energy' || selectedMarket.value === 'mFRR-energy') {
                    lodashSet(tempRegulationPrice, item._id, {
                        type: el === 'normalBids' ? 'normal' : 'bundle',
                        regulationPrice: lodashGet(item, ['primary', 'regulationPrice'], []),
                    });
                    temp.action = (
                        <div
                            onClick={() =>
                                setModal({
                                    status: true,
                                    modalId: 'updateEnergiBid',
                                    data: item,
                                })
                            }
                        >
                            <IconComponent icon={UilPen} tooltip={'Edit'} />
                        </div>
                    );
                } else {
                    temp.tempBidType = el;
                    temp.select = getCheckBox(temp);
                }

                if (selectedMarket.value === 'mFRR-energy') {
                    temp.energyBidType = 'Direct';
                    lodashSet(tempEnergyBidType, [item._id], 'direct');
                }

                el === 'normalBids' ? normalBids.push(temp) : bundleBids.push(temp);
            });
        });
        setBidsData({ normalBids, bundleBids });
        setEnergyBidsToPost(tempRegulationPrice);
        setEnergyBidType(tempEnergyBidType);
    };

    const handleSubmit = () => {
        if (selectedBidCred.value) {
            if (selectedMarket.value.includes('energy') && !Object.keys(energyBidsToPost).length) {
                toast.error('No energy bids to post.');
                return;
            }
            if (selectedMarket.value === 'FCR-D-FT' && type === 'secondary') {
                if (
                    postBidDeadLine.currentCETTime.isBetween(
                        lodashGet(postBidDeadLine, [selectedMarket.value, type, 'start']),
                        lodashGet(postBidDeadLine, [selectedMarket.value, type, 'end'])
                    )
                ) {
                    postBid();
                } else toast.error('Can not post secodary bid out of the timeline.');
            } else {
                if (!postBidDeadLine.currentCETTime.isAfter(lodashGet(postBidDeadLine, [selectedMarket.value, type]))) postBid();
                else toast.error('Can not post bid after deadline.');
            }
        }
    };

    const postBid = () => {
        const excludedCapBidsId = [];
        if (selectedMarket?.value === 'aFRR-capacity' || selectedMarket?.value === 'mFRR-capacity') {
            for (const key in bidsData) {
                lodashGet(bidsData, [key], []).map((bid) => {
                    if (!bid.checked) excludedCapBidsId.push(bid._id);
                });
            }
        }

        const payload = {
            bidCredId: selectedBidCred.value,
            bidDate: momentTimeFormater(date).format('YYYY-MM-DD'),
            market: selectedMarket.value,
            type,
            ...(selectedMarket?.value === 'aFRR-energy' || selectedMarket?.value === 'mFRR-energy' ? { energyBidsToPost } : {}),
            ...(selectedMarket?.value === 'mFRR-energy' ? { energyBidType } : {}),
            excludedCapBidsId: excludedCapBidsId,
        };
        service.PostBidEnerginet(payload, startLoader, handlePostSuccess, handleError, stopLoader);
    };

    const handlePostSuccess = () => {
        toast.success('Bids Posted Successfuly');
        closeModal();
        getBids();
    };

    const handleError = (err) => toast.error(lodashGet(err, ['response', 'data', 'message']));

    const closeEditModal = () => setModal({ status: false, modalId: null, data: null });

    return (
        <div>
            <div className={bidClasses.MainModalContainer}>
                <div className={bidClasses.DateContainer}>
                    <Typography content={`Post Bids to Energinet (${selectedMarket?.label})`} />
                    <div className={bidClasses.Flex}>
                        <div className={allDeviceClasses.FieldControl2}>
                            <label>Market</label>
                            <div className={bidClasses.DropdownContainer}>
                                <DropdownComponent defaultValue={selectedMarket} options={MARKET_OPTIONS} onChange={(m) => setSelectedMarket(m)} />
                            </div>
                        </div>
                        <div className={allDeviceClasses.FieldControl2}>
                            <label>Credential</label>
                            <div className={bidClasses.DropdownContainer}>
                                <DropdownComponent defaultValue={selectedBidCred} options={bidCredOption} onChange={(cred) => setSelectedBidCred(cred)} />
                            </div>
                        </div>
                        <div className={allDeviceClasses.FieldControl2}>
                            <label>Date</label>
                            <div className="modal-date-picker" style={{ width: '8vw' }}>
                                <DatePicker date={date} setDate={(d) => setDate(d)} disableFutureDate={false} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className={bidClasses.MainContainer}>
                    <div className={bidClasses.Flex} style={{ justifyContent: 'space-between' }}>
                        <Typography content={'Normal bids'} size={14} />
                        {lastPostedAt && <div>Last Posted (CET): {momentTimeFormater(lastPostedAt).format('YYYY-MM-DD HH:mm')}</div>}
                    </div>
                    <Table head={tableHeadersAndKeys?.headers} keys={tableHeadersAndKeys?.keys} data={bidsData.normalBids} />
                </div>

                <div className={bidClasses.MainContainer}>
                    <Typography content={'Bundle bids'} size={14} />
                    <Table head={tableHeadersAndKeys?.bundleBidsHeaders} keys={tableHeadersAndKeys?.bundleBidsKeys} data={bidsData.bundleBids} />
                </div>
                <div>
                    <div className={allDeviceClasses.ButtonContainer}>
                        <div onClick={closeModal}>
                            <button type="button" className="btn-secondary">
                                Cancel
                            </button>
                        </div>
                        <div onClick={() => (lastPostedAt ? setConfirmModal(true) : handleSubmit())}>
                            <button type="submit" className="btn-primary">
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <DeleteModal
                deletefunction={handleSubmit}
                opendeleteModal={confirmModal}
                setOpenDeleteModal={setConfirmModal}
                title={<div style={{ textAlign: 'center' }}>Are you sure you wish to submit your bid?</div>}
                text={'A bid has already been recorded for your selected options.'}
            />
            {modal.status && (
                <ModalComponent isOpen={modal.status} setOpen={() => {}}>
                    {modal.modalId === 'updateEnergiBid' && (
                        <UpdateAfrrEnergyBidHour
                            bid={modal.data}
                            energyBidsToPost={energyBidsToPost}
                            setEnergyBidsToPost={setEnergyBidsToPost}
                            closeModal={closeEditModal}
                            market={selectedMarket?.value}
                            setBidsData={setBidsData}
                            setEnergyBidType={setEnergyBidType}
                        />
                    )}
                </ModalComponent>
            )}
        </div>
    );
};

export default PostBid;
